import { turnKind } from '../shared/types';
import type {
  Attempt, ChoiceTurn, DrillKey, DrillKeyItem, DrillKind, DrillResult, ItemResult,
  OrgConfig, RedFlagMiss, Severity,
} from '../shared/types';

/**
 * Severity of a persona confusion (§5.6). Unlisted pairs default to `moderate`;
 * the map is content, so the engine never enumerates persona ids itself.
 */
export function confusionSeverity(
  config: OrgConfig,
  truePersona: string,
  selected: string
): Severity {
  if (truePersona === selected) return 'minor';
  return config.personaConfusionSeverity[`${truePersona}__${selected}`] ?? 'moderate';
}

/**
 * Credit for one persona-drill item.
 *
 * A correct answer is worth full credit. A blended item's acceptable-but-not-
 * preferred answer takes the configured partial credit. Everything else is
 * graded down by how wrong the confusion is (§5.6, §12.2) — reading a guarded
 * prospect as a dreamer costs the whole item, reading a dreamer as drowning
 * costs a quarter of it.
 */
export function personaItemCredit(
  config: OrgConfig,
  preferred: string,
  acceptable: string[],
  selected: string
): { credit: number; severity: Severity | null } {
  if (selected === preferred) return { credit: 1, severity: null };
  if (acceptable.includes(selected)) {
    const partial = config.drill?.acceptableAnswerCredit ?? 0.6;
    return { credit: partial, severity: null };
  }
  const severity = confusionSeverity(config, preferred, selected);
  const penalty = config.personaSeverityPenalty[severity] ?? 1;
  return { credit: Math.max(0, 1 - penalty), severity };
}

/**
 * Credit for an item graded right-or-wrong. §13.1 defines classificationScore as
 * correct classifications over classification nodes, so objection items are
 * binary; an authored acceptable answer counts as correct. Compliance and
 * financial items grade the same way — what varies is the weight, not the
 * credit (§12.4).
 */
export function binaryItemCredit(
  preferred: string,
  acceptable: string[],
  selected: string
): { credit: number; severity: Severity | null } {
  const correct = selected === preferred || acceptable.includes(selected);
  return { credit: correct ? 1 : 0, severity: null };
}

/** Kept as the objection-drill name; §11.4 scores classification this way. */
export const objectionItemCredit = binaryItemCredit;

/**
 * §12.4 — boundary items are weighted double: knowing what not to say matters
 * more than knowing the definition. Everything else carries unit weight.
 */
export function itemWeight(item: DrillKeyItem, config: OrgConfig): number {
  return item.boundary ? (config.drill?.boundaryItemWeight ?? 2) : 1;
}

/** The threshold a drill kind is judged against (§8.2 certification). */
export function thresholdFor(drillKind: DrillKey['drillKind'], config: OrgConfig): number {
  const c = config.certification;
  switch (drillKind) {
    case 'persona': return c.passingPersonaScore;
    case 'objection': return c.passingClassificationScore;
    case 'financial': return c.passingFinancialScore;
    case 'compliance': return c.passingComplianceScore;
    // §6.9 — a higher bar than the rest. The words are finite, written down,
    // and there is nothing to reason out: not knowing them is not knowing them.
    case 'language': return c.passingLanguageScore ?? 0.85;
    // §6.10 — the advanced track. Not a gate to anything, so the bar is about
    // whether the unit landed rather than about whether somebody may sell.
    case 'buying_signal':
    case 'paired_comparison':
    case 'discrimination':
      return c.passingTacticsScore ?? 0.8;
  }
}

const SCORE_FIELD = {
  persona: 'personaScore',
  objection: 'classificationScore',
  financial: 'financialScore',
  compliance: 'complianceScore',
  language: 'languageScore',
  buying_signal: 'tacticsScore',
  paired_comparison: 'tacticsScore',
  discrimination: 'tacticsScore',
} as const;

/**
 * Why the attempt failed, named by the thing it was measuring.
 *
 * Kept beside SCORE_FIELD and keyed the same way, so a new drill kind is a
 * compile error here rather than an `undefined` pushed into a rep's list of
 * reasons and rendered as a blank line.
 */
const FAILURE_REASON: Record<DrillKind, string> = {
  persona: 'persona_below_threshold',
  objection: 'classification_below_threshold',
  financial: 'financial_below_threshold',
  compliance: 'compliance_below_threshold',
  language: 'language_below_threshold',
  buying_signal: 'tactics_below_threshold',
  paired_comparison: 'tactics_below_threshold',
  discrimination: 'tactics_below_threshold',
};

const selectionOf = (turn: ChoiceTurn): string | null =>
  turn.classificationSelected ?? turn.personaSelected ?? turn.optionId ?? null;

/**
 * Score a drill attempt (§12.1). Drills reuse the attempt document, the scoring
 * trigger and the rollups unchanged — this adds the type, and nothing downstream
 * changes.
 *
 * Pure: everything it needs arrives as arguments, so the scoring rules are
 * testable without an emulator and a stored attempt can be re-scored from its
 * inputs (§1.1).
 */
export function scoreDrill(attempt: Attempt, key: DrillKey, config: OrgConfig): DrillResult {
  const byItem = new Map(key.items.map((i) => [i.id, i]));
  const seen = new Set<string>();
  const itemResults: ItemResult[] = [];
  const confusion: Record<string, number> = {};
  const redFlagMisses: RedFlagMiss[] = [];

  let creditTotal = 0;
  let weightTotal = 0;
  let correctCount = 0;
  let severeConfusionCount = 0;
  let deferredTurns = 0;
  let boundaryItemsScored = 0;

  for (const turn of attempt.turns ?? []) {
    // §8.6 — a free-text turn is carried through untouched so a v2 AI-scored
    // turn lands in the same transcript rather than breaking this scorer.
    if (turnKind(turn) === 'free_text') { deferredTurns++; continue; }

    const choice = turn as ChoiceTurn;
    const item = byItem.get(choice.nodeId);
    if (!item) continue;                 // a turn for content that is no longer in the key
    if (seen.has(item.id)) continue;     // first answer stands; no re-answering
    seen.add(item.id);

    const selected = selectionOf(choice);
    const acceptable = item.acceptableIds ?? [];

    let credit = 0;
    let severity: Severity | null = null;
    if (selected !== null) {
      const graded = key.drillKind === 'persona'
        ? personaItemCredit(config, item.preferredId, acceptable, selected)
        : binaryItemCredit(item.preferredId, acceptable, selected);
      credit = graded.credit;
      severity = graded.severity;
    }

    const correct = selected === item.preferredId;
    const acceptedAnswer = correct || (selected !== null && acceptable.includes(selected));
    if (correct) correctCount++;
    if (severity === 'severe') severeConfusionCount++;

    const weight = itemWeight(item, config);
    if (item.boundary) boundaryItemsScored++;
    creditTotal += credit * weight;
    weightTotal += weight;

    // §6.8 — a red-flag item must be answered correctly regardless of the
    // aggregate. The miss is recorded with its topic, the same way a simulation
    // compliance trap is.
    if (item.redFlag && !acceptedAnswer) {
      redFlagMisses.push({ itemId: item.id, topic: item.topic ?? 'unspecified', selected });
    }

    if (selected !== null) {
      const pairKey = `${item.preferredId}__${selected}`;
      confusion[pairKey] = (confusion[pairKey] ?? 0) + 1;
    }

    itemResults.push({
      itemId: item.id,
      selected,
      preferred: item.preferredId,
      correct,
      acceptable: selected !== null && acceptable.includes(selected),
      credit,
      weight,
      severity,
      ...(item.topic ? { topic: item.topic } : {}),
      ...(item.redFlag ? { redFlag: true } : {}),
      ...(item.boundary ? { boundary: true } : {}),
    });
  }

  // Items the rep never answered still count against them; a drill is scored
  // over the bank, not over what they chose to attempt. An unanswered red-flag
  // item is a miss for the same reason.
  const itemsScored = key.items.length;
  const itemsUnanswered = itemsScored - itemResults.length;
  for (const item of key.items) {
    if (seen.has(item.id)) continue;
    weightTotal += itemWeight(item, config);
    if (item.redFlag) {
      redFlagMisses.push({ itemId: item.id, topic: item.topic ?? 'unspecified', selected: null });
    }
  }

  const score = weightTotal === 0 ? 0 : creditTotal / weightTotal;
  const threshold = thresholdFor(key.drillKind, config);

  const failureReasons: string[] = [];
  if (score < threshold) {
    failureReasons.push(FAILURE_REASON[key.drillKind]);
  }
  // §6.8 — independent of passingComplianceScore. A rep at 95% who misses a
  // red-flag item has not passed.
  if (redFlagMisses.length) failureReasons.push('red_flag_miss');

  const result: DrillResult = {
    drillKind: key.drillKind,
    itemsScored,
    itemsUnanswered,
    deferredTurns,
    itemResults,
    correctCount,
    redFlagMisses,
    boundaryItemsScored,
    severeConfusionCount,
    confusion,
    passed: failureReasons.length === 0,
    failureReasons,
    threshold,
    rubricVersion: key.rubricVersion,
    contentVersion: key.contentVersion,
    scoredAt: new Date().toISOString(),
  };

  result[SCORE_FIELD[key.drillKind]] = score;
  return result;
}

/**
 * The score a drill result is judged on, whichever subscore it produced.
 *
 * Read off SCORE_FIELD rather than listed by hand. A drill kind that writes a
 * subscore this function has not heard of does not fail loudly — it reports
 * zero, to the rep and to every rollup, and a rep who answered everything
 * correctly is told they scored nothing.
 */
export const drillScore = (result: DrillResult): number => {
  for (const field of Object.values(SCORE_FIELD)) {
    const value = result[field];
    if (typeof value === 'number') return value;
  }
  return 0;
};
